import { useCallback, useEffect, useState } from 'react';
import {
  getReferralConversionHistory,
  getLegacyReferralSummary,
  getReferralRewardHistory,
  getReferralSummary,
} from '../services/referralService';

const emptySummary = {
  referral_code: '',
  referral_url: '',
  active_downlines: 0,
  required_deposit: 0,
  required_turnover: 0,
  lifetime_cash_rewards: 0,
  cash_rewards: 0,
  referral_turnover: 0,
  referral_points: 0,
  min_convert_points: 10,
  convert_rate: 1,
};

function normalizeListResponse(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

export default function useReferralData(userData) {
  const [summary, setSummary] = useState({
    ...emptySummary,
    referral_code: userData?.referral_code || '',
    referral_url: userData?.referral_code
      ? `${window.location.origin}/referral_code?referral_code=${userData.referral_code}`
      : '',
    cash_rewards: Number(userData?.cash_rewards || userData?.cash_bonus || 0),
    lifetime_cash_rewards: Number(userData?.lifetime_cash_rewards || userData?.cash_bonus || 0),
    referral_points: Number(userData?.referral_points || 0),
  });
  const [rewardHistory, setRewardHistory] = useState([]);
  const [conversionHistory, setConversionHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const token = localStorage.getItem('token');
    const remoteId = localStorage.getItem('remote_id');

    if (!token || !remoteId) {
      setLoading(false);
      setError('Please login again');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let summaryData = {};
      let summaryLoaded = false;

      try {
        const summaryRes = await getReferralSummary(remoteId, token);
        summaryData = summaryRes?.data || summaryRes || {};
        summaryLoaded = true;
      } catch (summaryError) {
        try {
          const legacySummaryRes = await getLegacyReferralSummary(remoteId, token);
          summaryData = legacySummaryRes?.data || {};
          summaryLoaded = true;
        } catch (_legacyError) {
          summaryLoaded = false;
        }
      }

      const [rewardRes, conversionRes] = await Promise.allSettled([
        getReferralRewardHistory(remoteId, token),
        getReferralConversionHistory(remoteId, token),
      ]);

      setSummary((prev) => ({
        ...prev,
        ...emptySummary,
        ...summaryData,
        referral_code: summaryData.referral_code || prev.referral_code,
        referral_url:
          summaryData.referral_url ||
          (summaryData.referral_code
            ? `${window.location.origin}/referral_code?referral_code=${summaryData.referral_code}`
            : prev.referral_url),
      }));

      setRewardHistory(
        rewardRes.status === 'fulfilled' ? normalizeListResponse(rewardRes.value) : []
      );
      setConversionHistory(
        conversionRes.status === 'fulfilled' ? normalizeListResponse(conversionRes.value) : []
      );

      if (!summaryLoaded && !userData?.referral_code) {
        setError('Referral data is temporarily unavailable');
      }
    } catch (err) {
      setError(err?.message || 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  }, [userData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    summary,
    rewardHistory,
    conversionHistory,
    loading,
    error,
    refresh,
  };
}
